import { CommonModule } from '@angular/common'
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  OnDestroy,
  OnInit,
  output,
  signal
} from '@angular/core'
import { VisioComponentsModule } from '@visio-ai/ui-kit'
import { Subject } from 'rxjs'
import { debounceTime } from 'rxjs/operators'
import { CapturingWindowWeek } from '../../../../_services/camera/camera.model'
import { ScheduleRule } from '../../models/schedule-rule.model'
import { CameraService } from '../../services/camera.service'

@Component({
  selector: 'app-schedule-rule-card',
  standalone: true,
  imports: [CommonModule, VisioComponentsModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './schedule-rule-card.component.html',
  styleUrl: './schedule-rule-card.component.scss'
})
export class ScheduleRuleCardComponent implements OnInit, OnDestroy {
  private cameraService = inject(CameraService)

  scheduleRule = input.required<ScheduleRule>()
  shopName = input.required<string>()
  userName = input.required<string>()
  // Signal to store capturing windows for the camera
  private capturingWindows = signal<CapturingWindowWeek | null>(null)

  // Signal to store camera name
  private cameraName = signal<string | null>(null)

  // Computed signal to check which weekdays have recording windows
  weekdayHasRecordingWindows = computed(() => {
    const windows = this.capturingWindows()

    return {
      dom: this.cameraService.hasRecordingWindowsForWeekday(windows, 0),
      seg: this.cameraService.hasRecordingWindowsForWeekday(windows, 1),
      ter: this.cameraService.hasRecordingWindowsForWeekday(windows, 2),
      qua: this.cameraService.hasRecordingWindowsForWeekday(windows, 3),
      qui: this.cameraService.hasRecordingWindowsForWeekday(windows, 4),
      sex: this.cameraService.hasRecordingWindowsForWeekday(windows, 5),
      sab: this.cameraService.hasRecordingWindowsForWeekday(windows, 6)
    }
  })

  product = computed(() => {
    return this.scheduleRule().product.toLocaleUpperCase()
  })

  // Computed signal for camera display name
  camera = computed(() => {
    return this.cameraName() || this.scheduleRule().camera_id
  })

  cardTitle = computed(() => `${this.product()} | ${this.shopName()} [${this.camera()}]`)

  weekdaysMap = new Map([
    [0, 'Dom'],
    [1, 'Seg'],
    [2, 'Ter'],
    [3, 'Qua'],
    [4, 'Qui'],
    [5, 'Sex'],
    [6, 'Sáb']
  ])

  // Array of weekdays for iteration in template
  weekdays = [
    { index: 0, label: 'Dom', key: 'dom' as const },
    { index: 1, label: 'Seg', key: 'seg' as const },
    { index: 2, label: 'Ter', key: 'ter' as const },
    { index: 3, label: 'Qua', key: 'qua' as const },
    { index: 4, label: 'Qui', key: 'qui' as const },
    { index: 5, label: 'Sex', key: 'sex' as const },
    { index: 6, label: 'Sáb', key: 'sab' as const }
  ]

  // Track pending weekday changes for optimistic UI
  private pendingToggles = signal(new Set<number>())

  // Create a computed signal for each weekday with optimistic updates
  isWeekdayActive = computed(() => {
    const baseWeekdays = new Set(this.scheduleRule().weekdays)
    const pending = this.pendingToggles()

    // Apply pending toggles
    pending.forEach(weekday => {
      if (baseWeekdays.has(weekday)) {
        baseWeekdays.delete(weekday)
      } else {
        baseWeekdays.add(weekday)
      }
    })

    return {
      dom: baseWeekdays.has(0),
      seg: baseWeekdays.has(1),
      ter: baseWeekdays.has(2),
      qua: baseWeekdays.has(3),
      qui: baseWeekdays.has(4),
      sex: baseWeekdays.has(5),
      sab: baseWeekdays.has(6)
    }
  })

  updated_at = computed(() => {
    return new Date(this.scheduleRule().updated_at!).toLocaleString('pt-BR')
  })

  switchDisabled = computed(() => !this.scheduleRule().weekdays?.length)

  toggleActive = output<boolean>()
  updateWeekdays = output<number[]>()
  requestUpdateFromAdmin = output()
  updateSchedulingRequirements = output<string>()

  isEditingRequirements = signal(false)
  editedRequirements = signal('')

  startEditRequirements() {
    this.editedRequirements.set(this.scheduleRule().schedule_requirements || '')
    this.isEditingRequirements.set(true)
  }

  saveRequirements() {
    this.updateSchedulingRequirements.emit(this.editedRequirements())
    this.isEditingRequirements.set(false)
  }

  cancelEditRequirements() {
    this.isEditingRequirements.set(false)
  }

  private weekdayChanges$ = new Subject<void>()

  constructor() {
    this.weekdayChanges$.pipe(debounceTime(1000)).subscribe(() => {
      const currentWeekdays = [...(this.scheduleRule().weekdays || [])]
      const pending = this.pendingToggles()

      // Apply all pending changes
      pending.forEach(weekday => {
        const index = currentWeekdays.indexOf(weekday)
        if (index > -1) {
          currentWeekdays.splice(index, 1)
        } else {
          currentWeekdays.push(weekday)
        }
      })

      // Sort the array to maintain order
      currentWeekdays.sort((a, b) => a - b)

      this.updateWeekdays.emit(currentWeekdays)

      // Clear pending toggles after emitting
      this.pendingToggles.set(new Set())
    })
  }

  ngOnInit() {
    // Initial load of capturing windows
    const cameraId = this.scheduleRule().camera_id
    const shopId = this.scheduleRule().shop_id

    if (cameraId && shopId) {
      this.loadCapturingWindows(shopId, cameraId)
    }
  }

  private loadCapturingWindows(shopId: string, cameraId: string) {
    this.cameraService.getCamera(shopId, cameraId).subscribe(camera => {
      if (camera) {
        this.capturingWindows.set(camera.capturing_windows || null)
        this.cameraName.set(camera.name)
      }
    })
  }

  isWeekdayChecked(key: 'dom' | 'seg' | 'ter' | 'qua' | 'qui' | 'sex' | 'sab'): boolean {
    return this.isWeekdayActive()[key]
  }

  isWeekdayDisabled(key: 'dom' | 'seg' | 'ter' | 'qua' | 'qui' | 'sex' | 'sab'): boolean {
    return !this.weekdayHasRecordingWindows()[key]
  }

  getTooltipText(weekday: number): string {
    // If weekday has no recording windows, show that message
    if (!this.cameraService.hasRecordingWindowsForWeekday(this.capturingWindows(), weekday)) {
      return 'Câmera não possui janelas de gravação para este dia da semana'
    }

    // Otherwise, no tooltip needed
    return ''
  }

  toggleWeekday(weekday: number) {
    // Don't allow toggling if the weekday has no recording windows
    if (!this.cameraService.hasRecordingWindowsForWeekday(this.capturingWindows(), weekday)) {
      return
    }

    // Update pending toggles for optimistic UI
    const current = new Set(this.pendingToggles())
    if (current.has(weekday)) {
      current.delete(weekday)
    } else {
      current.add(weekday)
    }
    this.pendingToggles.set(current)

    // Trigger debounced update
    this.weekdayChanges$.next()
  }

  ngOnDestroy() {
    this.weekdayChanges$.complete()
  }
}
